import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  HelpOutline as HelpIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import schedulesService from '../../services/schedulesService';

/**
 * ScheduleCreationWizard - Wizard multi-step para criação de agendas
 * 
 * Props:
 * - open: boolean - Se o wizard está aberto
 * - onClose: function - Callback ao fechar
 * - onSuccess: function - Callback ao finalizar com sucesso
 * - placeName: string - Nome do local
 * - placeId: string - ID do local
 * - professionals: array - Lista de profissionais { id, name }
 */

// Função helper para delay entre chamadas de API
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const API_DELAY_MS = 1000;

// Dias da semana
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Segunda-feira', value: 1 },
  { key: 'tuesday', label: 'Terça-feira', value: 2 },
  { key: 'wednesday', label: 'Quarta-feira', value: 3 },
  { key: 'thursday', label: 'Quinta-feira', value: 4 },
  { key: 'friday', label: 'Sexta-feira', value: 5 },
  { key: 'saturday', label: 'Sábado', value: 6 },
  { key: 'sunday', label: 'Domingo', value: 7 },
];

// Horários padrão
const DEFAULT_SINGLE_SHIFT = { start: '09:00', end: '17:00' };
const DEFAULT_DOUBLE_SHIFT = {
  morning: { start: '08:30', end: '12:00' },
  afternoon: { start: '13:30', end: '18:00' },
};

export default function ScheduleCreationWizard({ 
  open, 
  onClose, 
  onSuccess,
  placeName = '',
  placeId = '',
  professionals = [],
}) {
  const { getLabel } = useThemeCustom();
  const placeSingular = getLabel('placesSingular')?.toLowerCase() || 'local';
  const placePreposition = getLabel('placesPreposition') || 'do';
  const placeArticle = getLabel('placesArticle') || 'o';
  
  // Estado do wizard
  const [activeStep, setActiveStep] = useState(0);
  
  // Configuração de agenda por profissional
  // { [profIndex]: { days: { monday: { enabled, singleShift, times: {...} }, ... } } }
  const [professionalSchedules, setProfessionalSchedules] = useState({});
  
  // Estado de criação
  const [creating, setCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [creationStatus, setCreationStatus] = useState([]);
  const [creationError, setCreationError] = useState(null);
  const [creationComplete, setCreationComplete] = useState(false);
  
  // Agendas criadas (para mostrar links)
  const [createdSchedules, setCreatedSchedules] = useState([]);

  // Calcular total de steps: 1 por profissional + resumo + criação
  const totalSteps = professionals.length + 2; // profissionais + resumo + criação
  
  // Labels dos steps
  const getStepLabels = useMemo(() => {
    const labels = professionals.map(p => p.name);
    labels.push('Resumo');
    labels.push('Criação');
    return labels;
  }, [professionals]);

  // Inicializar schedules para cada profissional
  const handleDialogEnter = useCallback(() => {
    console.log('[ScheduleCreationWizard] 🚀 Dialog aberto - Inicializando wizard');
    console.log('[ScheduleCreationWizard] Profissionais:', professionals);
    console.log('[ScheduleCreationWizard] Local:', placeName, placeId);
    
    // Resetar estado
    setActiveStep(0);
    setCreating(false);
    setCreationProgress(0);
    setCreationStatus([]);
    setCreationError(null);
    setCreationComplete(false);
    setCreatedSchedules([]);
    
    // Inicializar configuração de cada profissional
    const initialSchedules = {};
    professionals.forEach((prof, index) => {
      initialSchedules[index] = {
        days: DAYS_OF_WEEK.reduce((acc, day) => {
          acc[day.key] = {
            enabled: false,
            singleShift: true,
            times: {
              single: { ...DEFAULT_SINGLE_SHIFT },
              morning: { ...DEFAULT_DOUBLE_SHIFT.morning },
              afternoon: { ...DEFAULT_DOUBLE_SHIFT.afternoon },
            },
          };
          return acc;
        }, {}),
      };
    });
    setProfessionalSchedules(initialSchedules);
  }, [professionals, placeName, placeId]);

  // Toggle dia da semana
  const toggleDay = (profIndex, dayKey) => {
    console.log(`[ScheduleCreationWizard] Toggle dia ${dayKey} para profissional ${profIndex}`);
    setProfessionalSchedules(prev => ({
      ...prev,
      [profIndex]: {
        ...prev[profIndex],
        days: {
          ...prev[profIndex].days,
          [dayKey]: {
            ...prev[profIndex].days[dayKey],
            enabled: !prev[profIndex].days[dayKey].enabled,
          },
        },
      },
    }));
  };

  // Alternar turno único/duplo
  const toggleShiftMode = (profIndex, dayKey, singleShift) => {
    console.log(`[ScheduleCreationWizard] Modo turno ${singleShift ? 'único' : 'duplo'} para ${dayKey}`);
    setProfessionalSchedules(prev => ({
      ...prev,
      [profIndex]: {
        ...prev[profIndex],
        days: {
          ...prev[profIndex].days,
          [dayKey]: {
            ...prev[profIndex].days[dayKey],
            singleShift,
          },
        },
      },
    }));
  };

  // Atualizar horário
  const updateTime = (profIndex, dayKey, timeType, field, value) => {
    console.log(`[ScheduleCreationWizard] Atualizando ${timeType}.${field} = ${value}`);
    setProfessionalSchedules(prev => ({
      ...prev,
      [profIndex]: {
        ...prev[profIndex],
        days: {
          ...prev[profIndex].days,
          [dayKey]: {
            ...prev[profIndex].days[dayKey],
            times: {
              ...prev[profIndex].days[dayKey].times,
              [timeType]: {
                ...prev[profIndex].days[dayKey].times[timeType],
                [field]: value,
              },
            },
          },
        },
      },
    }));
  };

  // Navegação
  const handleNext = () => {
    console.log(`[ScheduleCreationWizard] Avançando do step ${activeStep}`);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    console.log(`[ScheduleCreationWizard] Voltando do step ${activeStep}`);
    setActiveStep(prev => prev - 1);
  };

  // Verificar se profissional atual tem ao menos um dia selecionado
  const currentProfHasSelectedDays = useMemo(() => {
    if (activeStep >= professionals.length) return true;
    const schedule = professionalSchedules[activeStep];
    if (!schedule) return false;
    return Object.values(schedule.days).some(d => d.enabled);
  }, [activeStep, professionalSchedules, professionals.length]);

  // Gerar resumo para o step de resumo
  const getSummary = useMemo(() => {
    return professionals.map((prof, index) => {
      const schedule = professionalSchedules[index];
      if (!schedule) return { prof, days: [] };
      
      const enabledDays = DAYS_OF_WEEK.filter(d => schedule.days[d.key]?.enabled);
      return {
        prof,
        scheduleName: `Agenda de ${prof.name} em ${placeName}`,
        days: enabledDays.map(d => {
          const dayConfig = schedule.days[d.key];
          return {
            ...d,
            singleShift: dayConfig.singleShift,
            times: dayConfig.times,
          };
        }),
      };
    });
  }, [professionals, professionalSchedules, placeName]);

  // Helper para agrupar dias por horário (consolidação de turnos)
  const groupDaysByTime = (days) => {
    const groups = {};
    
    for (const day of days) {
      if (day.singleShift) {
        // Turno único - chave baseada no horário
        const key = `single_${day.times.single.start}_${day.times.single.end}`;
        if (!groups[key]) {
          groups[key] = {
            type: 'single',
            startTime: day.times.single.start,
            endTime: day.times.single.end,
            days: [],
          };
        }
        groups[key].days.push(day);
      } else {
        // Dois turnos - manhã e tarde separados
        const morningKey = `morning_${day.times.morning.start}_${day.times.morning.end}`;
        if (!groups[morningKey]) {
          groups[morningKey] = {
            type: 'morning',
            startTime: day.times.morning.start,
            endTime: day.times.morning.end,
            days: [],
          };
        }
        groups[morningKey].days.push(day);
        
        const afternoonKey = `afternoon_${day.times.afternoon.start}_${day.times.afternoon.end}`;
        if (!groups[afternoonKey]) {
          groups[afternoonKey] = {
            type: 'afternoon',
            startTime: day.times.afternoon.start,
            endTime: day.times.afternoon.end,
            days: [],
          };
        }
        groups[afternoonKey].days.push(day);
      }
    }
    
    return Object.values(groups);
  };

  // Criar agendas
  const startCreation = async () => {
    console.log('[ScheduleCreationWizard] 🏁 INICIANDO CRIAÇÃO DE AGENDAS');
    
    setCreating(true);
    setCreationProgress(0);
    setCreationStatus([]);
    setCreationError(null);
    setActiveStep(totalSteps - 1);
    
    try {
      // Contar total de operações (turnos consolidados)
      let totalOps = 0;
      getSummary.forEach(s => {
        if (s.days.length > 0) {
          totalOps++; // Criar agenda
          // Contar turnos consolidados a criar
          const shiftGroups = groupDaysByTime(s.days);
          totalOps += shiftGroups.length;
        }
      });
      
      console.log(`[ScheduleCreationWizard] Total de operações: ${totalOps}`);
      
      let completedOps = 0;
      const updateProgress = () => {
        completedOps++;
        setCreationProgress((completedOps / totalOps) * 100);
      };
      
      const schedules = [];
      
      for (const summary of getSummary) {
        if (summary.days.length === 0) {
          console.log(`[ScheduleCreationWizard] Pulando ${summary.prof.name} - sem dias selecionados`);
          continue;
        }
        
        // Criar agenda
        const scheduleName = summary.scheduleName;
        console.log(`[ScheduleCreationWizard] 📅 Criando agenda: ${scheduleName}`);
        setCreationStatus(prev => [...prev, { text: `Criando agenda "${scheduleName}"...`, done: false }]);
        
        const schedule = await schedulesService.create({
          placeId,
          professionalId: summary.prof.id,
          name: scheduleName,
        });
        console.log(`[ScheduleCreationWizard] ✅ Agenda criada:`, schedule);
        
        schedules.push({
          ...schedule,
          professionalName: summary.prof.name,
        });
        
        setCreationStatus(prev => {
          const updated = [...prev];
          updated[updated.length - 1].done = true;
          return updated;
        });
        updateProgress();
        await delay(API_DELAY_MS);
        
        // Agrupar dias por horário e criar turnos consolidados
        const shiftGroups = groupDaysByTime(summary.days);
        console.log(`[ScheduleCreationWizard] 📊 ${shiftGroups.length} turno(s) consolidado(s) a criar`);
        
        for (const group of shiftGroups) {
          // Montar nome do turno baseado nos dias
          const dayLabels = group.days.map(d => d.label.substring(0, 3)).join('/');
          const shiftTypeSuffix = group.type === 'morning' ? ' - Manhã' : (group.type === 'afternoon' ? ' - Tarde' : '');
          const shiftName = `${dayLabels}${shiftTypeSuffix}`;
          
          // Montar objeto com todos os dias do grupo como true
          const daysConfig = DAYS_OF_WEEK.reduce((acc, d) => {
            acc[d.key] = group.days.some(gd => gd.key === d.key);
            return acc;
          }, {});
          
          const shiftData = {
            scheduleId: schedule.id,
            name: shiftName,
            startTime: group.startTime,
            endTime: group.endTime,
            ...daysConfig,
            // Vincular serviços do profissional neste local
            serviceIds: summary.prof.serviceIds || [],
          };
          
          const daysText = group.days.map(d => d.label).join(', ');
          console.log(`[ScheduleCreationWizard] 🕐 Criando turno consolidado "${shiftName}" (${daysText}):`, shiftData);
          setCreationStatus(prev => [...prev, { 
            text: `Criando turno ${shiftName} (${group.startTime} - ${group.endTime})...`, 
            done: false 
          }]);
          
          await schedulesService.addShift(schedule.id, shiftData);
          
          setCreationStatus(prev => {
            const updated = [...prev];
            updated[updated.length - 1].done = true;
            return updated;
          });
          updateProgress();
          await delay(API_DELAY_MS);
        }
      }
      
      console.log('[ScheduleCreationWizard] ✅ CRIAÇÃO CONCLUÍDA!');
      setCreatedSchedules(schedules);
      setCreationProgress(100);
      setCreationComplete(true);
      setCreationStatus(prev => [...prev, { text: '✅ Todas as agendas criadas!', done: true, success: true }]);
      
    } catch (error) {
      console.error('[ScheduleCreationWizard] ❌ ERRO:', error);
      setCreationError(error.response?.data?.message || error.message || 'Erro ao criar agenda');
    } finally {
      setCreating(false);
    }
  };

  // Copiar link da agenda
  const copyScheduleLink = (schedule) => {
    const link = `https://share.agendario.com.br/schedule/${schedule.id}`;
    navigator.clipboard.writeText(link);
    console.log(`[ScheduleCreationWizard] Link copiado: ${link}`);
  };

  // Finalizar
  const handleFinish = () => {
    console.log('[ScheduleCreationWizard] 🎉 Finalizando wizard');
    onClose();
    if (creationComplete && onSuccess) {
      onSuccess(createdSchedules);
    }
  };

  // Render do conteúdo do step atual
  const renderStepContent = () => {
    // Steps dos profissionais
    if (activeStep < professionals.length) {
      const prof = professionals[activeStep];
      const schedule = professionalSchedules[activeStep] || { days: {} };
      
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Quais dias da semana <strong>{prof.name}</strong> estará atendendo?
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Turnos, horários e mais customizações podem ser editados depois de criada a agenda.
          </Alert>
          
          <FormGroup>
            {DAYS_OF_WEEK.map(day => {
              const dayConfig = schedule.days[day.key] || { enabled: false, singleShift: true, times: {} };
              
              return (
                <Paper 
                  key={day.key} 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    p: 2,
                    bgcolor: dayConfig.enabled ? 'action.selected' : 'transparent',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dayConfig.enabled}
                        onChange={() => toggleDay(activeStep, day.key)}
                      />
                    }
                    label={<Typography fontWeight={dayConfig.enabled ? 'bold' : 'normal'}>{day.label}</Typography>}
                  />
                  
                  {dayConfig.enabled && (
                    <Box sx={{ mt: 2, ml: 4 }}>
                      {/* Seletor turno único/duplo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RadioGroup
                          row
                          value={dayConfig.singleShift ? 'single' : 'double'}
                          onChange={(e) => toggleShiftMode(activeStep, day.key, e.target.value === 'single')}
                        >
                          <FormControlLabel 
                            value="single" 
                            control={<Radio size="small" />} 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <span>Turno único</span>
                                <Tooltip title="Turno único é útil para quem não para no horário de almoço">
                                  <IconButton size="small" sx={{ ml: 0.5 }}>
                                    <HelpIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          />
                          <FormControlLabel 
                            value="double" 
                            control={<Radio size="small" />} 
                            label="Dois turnos"
                          />
                        </RadioGroup>
                      </Box>
                      
                      {/* Inputs de horário */}
                      {dayConfig.singleShift ? (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <TextField
                            label="Início"
                            type="time"
                            size="small"
                            value={dayConfig.times.single?.start || DEFAULT_SINGLE_SHIFT.start}
                            onChange={(e) => updateTime(activeStep, day.key, 'single', 'start', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                          />
                          <TextField
                            label="Fim"
                            type="time"
                            size="small"
                            value={dayConfig.times.single?.end || DEFAULT_SINGLE_SHIFT.end}
                            onChange={(e) => updateTime(activeStep, day.key, 'single', 'end', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Turno da manhã
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label="Início"
                                type="time"
                                size="small"
                                value={dayConfig.times.morning?.start || DEFAULT_DOUBLE_SHIFT.morning.start}
                                onChange={(e) => updateTime(activeStep, day.key, 'morning', 'start', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 140 }}
                              />
                              <TextField
                                label="Fim"
                                type="time"
                                size="small"
                                value={dayConfig.times.morning?.end || DEFAULT_DOUBLE_SHIFT.morning.end}
                                onChange={(e) => updateTime(activeStep, day.key, 'morning', 'end', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 140 }}
                              />
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Turno da tarde
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label="Início"
                                type="time"
                                size="small"
                                value={dayConfig.times.afternoon?.start || DEFAULT_DOUBLE_SHIFT.afternoon.start}
                                onChange={(e) => updateTime(activeStep, day.key, 'afternoon', 'start', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 140 }}
                              />
                              <TextField
                                label="Fim"
                                type="time"
                                size="small"
                                value={dayConfig.times.afternoon?.end || DEFAULT_DOUBLE_SHIFT.afternoon.end}
                                onChange={(e) => updateTime(activeStep, day.key, 'afternoon', 'end', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 140 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </FormGroup>
        </Box>
      );
    }
    
    // Step Resumo
    if (activeStep === professionals.length) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Resumo das Agendas
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Confira as agendas que serão criadas. Você pode voltar para ajustar os horários.
          </Alert>
          
          {getSummary.map((summary, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                📅 {summary.scheduleName}
              </Typography>
              
              {summary.days.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  Nenhum dia selecionado - esta agenda não será criada
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {summary.days.map(day => (
                    <Chip 
                      key={day.key}
                      label={
                        day.singleShift 
                          ? `${day.label}: ${day.times.single?.start || '09:00'} - ${day.times.single?.end || '17:00'}`
                          : `${day.label}: ${day.times.morning?.start || '08:30'}-${day.times.morning?.end || '12:00'} / ${day.times.afternoon?.start || '13:30'}-${day.times.afternoon?.end || '18:00'}`
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      );
    }
    
    // Step Criação
    return (
      <Box sx={{ py: 2 }}>
        {creating && (
          <>
            <Typography variant="h6" gutterBottom>
              Criando agendas...
            </Typography>
            <LinearProgress variant="determinate" value={creationProgress} sx={{ mb: 2, height: 8, borderRadius: 1 }} />
          </>
        )}
        
        {creationComplete && (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CelebrationIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" color="success.main" gutterBottom>
                Agendas criadas com sucesso!
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Links de compartilhamento:
            </Typography>
            
            {createdSchedules.map(schedule => (
              <Paper key={schedule.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {schedule.name || `Agenda de ${schedule.professionalName}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      https://share.agendario.com.br/schedule/{schedule.id}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Copiar link">
                      <IconButton onClick={() => copyScheduleLink(schedule)}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Compartilhar">
                      <IconButton 
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({
                              title: schedule.name,
                              url: `https://share.agendario.com.br/schedule/${schedule.id}`,
                            });
                          }
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </>
        )}
        
        {creationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {creationError}
          </Alert>
        )}
        
        {/* Lista de status */}
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {creationStatus.map((status, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                py: 0.5,
                color: status.success ? 'success.main' : status.warning ? 'warning.main' : 'text.primary',
              }}
            >
              {status.done ? (
                <CheckIcon color={status.success ? 'success' : status.warning ? 'warning' : 'action'} fontSize="small" />
              ) : (
                <CircularProgress size={16} />
              )}
              <Typography variant="body2">{status.text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onEnter: handleDialogEnter,
      }}
    >
      <DialogTitle>
        Criar Agendas de Atendimento
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Stepper */}
        {activeStep < totalSteps - 1 && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {getStepLabels.slice(0, -1).map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Botão Voltar */}
        {activeStep > 0 && activeStep < totalSteps - 1 && !creating && (
          <Button 
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Voltar
          </Button>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        {/* Botão Cancelar */}
        {!creating && !creationComplete && (
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
        )}
        
        {/* Botão Próximo (steps profissionais) */}
        {activeStep < professionals.length && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!currentProfHasSelectedDays}
            endIcon={<ArrowForwardIcon />}
          >
            {activeStep < professionals.length - 1 ? 'Próximo Profissional' : 'Ver Resumo'}
          </Button>
        )}
        
        {/* Botão Criar (step resumo) */}
        {activeStep === professionals.length && (
          <Button
            variant="contained"
            color="success"
            onClick={startCreation}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Criar Agendas
          </Button>
        )}
        
        {/* Botão Concluir */}
        {creationComplete && (
          <Button
            variant="contained"
            color="success"
            onClick={handleFinish}
          >
            Concluir
          </Button>
        )}
        
        {/* Botão Fechar (erro) */}
        {creationError && (
          <Button onClick={onClose} color="error">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
